import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import Seo from "@/components/site/Seo";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/site/motion";
import { SectionHeading, CTABanner, FAQList } from "@/components/site/Blocks";
import { getDestination } from "@/data/destinations";
import { getService } from "@/data/services";
import { useT } from "@/i18n";

const EUSelector = ({ countries }) => {
  const [sel, setSel] = useState(0);
  const c = countries[sel];
  return (
    <section className="bg-navy py-24 text-ivory md:py-32" data-testid="eu-country-selector">
      <div className="container-lux">
        <SectionHeading light eyebrow="Member States" lines={["Choose your", <em key="e" className="italic text-gold">European address.</em>]} intro="Each member state sets its own residence rules. Select a country for an overview of popular routes — always verified against current official guidance during consultation." />
        <div className="mt-14 flex flex-wrap gap-3">
          {countries.map((x, i) => (
            <button key={x.name} type="button" onClick={() => setSel(i)} data-testid={`eu-country-${x.name.toLowerCase()}`} aria-pressed={i === sel} className={`border px-5 py-3 font-sans text-[12px] font-semibold uppercase tracking-[0.2em] transition-colors duration-500 ${i === sel ? "border-gold bg-gold text-navy" : "border-ivory/25 text-ivory/85 hover:border-gold hover:text-gold"}`}>{x.name}</button>
          ))}
        </div>
        <div key={c.name} className="mt-12 grid gap-8 border-t border-gold/25 pt-10 animate-in fade-in slide-in-from-bottom-2 duration-700 md:grid-cols-[0.6fr_1.4fr]" data-testid="eu-country-panel">
          <p className="font-serif text-5xl font-light text-gold md:text-6xl">{c.name}</p>
          <p className="text-lg leading-relaxed text-ivory/85">{c.text}</p>
        </div>
      </div>
    </section>
  );
};

export default function DestinationPage() {
  const { slug } = useParams();
  const { t } = useT();
  const d = getDestination(slug);
  if (!d) return <Navigate to="/" replace />;
  return (
    <>
      <Seo title={`${d.name} Immigration & Visa Advisory`} description={`${d.tagline} Explore ${d.name} visa and residence pathways with Immistop, subject to eligibility.`} />
      <PageHero eyebrow={`Destinations · ${d.city}`} lines={[d.name]} subtitle={d.tagline} image={d.hero} testid="destination-hero">
        <div className="flex flex-col gap-4 sm:flex-row">
          <Link to="/contact" className="btn-gold" data-testid="destination-book-btn"><span>{t("cta.book")}</span></Link>
          <Link to="/eligibility" className="btn-outline-light" data-testid="destination-eligibility-btn">{t("cta.eligibility")}</Link>
        </div>
      </PageHero>

      <section className="bg-ivory section-lux" data-testid="destination-pathways">
        <div className="container-lux">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
            <SectionHeading eyebrow="Overview" lines={["Immigration", "pathways."]} />
            <Reveal className="lg:pt-24"><p className="lede text-charcoal">{d.intro}</p></Reveal>
          </div>
          <div className="mt-24 grid gap-x-16 gap-y-20 md:mt-32 md:grid-cols-2 lg:grid-cols-3">
            {d.pathways.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08} className="border-t border-navy/10 pt-10">
                <span className="font-sans text-[11px] font-semibold tracking-[0.25em] text-gold-deep">0{i + 1}</span>
                <h3 className="mt-8 font-serif text-4xl font-light text-navy">{p.title}</h3>
                <p className="mt-5 text-[16px] font-light leading-relaxed text-charcoal-soft">{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {d.euCountries && <EUSelector countries={d.euCountries} />}

      <section className="bg-ivory-100 section-lux" data-testid="destination-services">
        <div className="container-lux">
          <SectionHeading eyebrow="Services Available" lines={[`How we assist in ${d.short}.`]} />
          <ul className="mt-20 border-t border-navy/10 md:mt-28">
            {d.services.map((s) => getService(s)).map((s, i) => (
              <Reveal as="li" key={s.slug} delay={i * 0.05}>
                <Link to={`/services/${s.slug}`} className="group grid items-baseline gap-4 border-b border-navy/10 py-10 md:grid-cols-[1fr_1fr_auto] md:gap-12" data-testid={`destination-service-${s.slug}`}>
                  <h3 className="font-serif text-4xl font-light text-navy transition-transform duration-700 group-hover:translate-x-3 md:text-5xl">{s.name}</h3>
                  <p className="max-w-md text-[16px] font-light leading-relaxed text-charcoal-soft">{s.short}</p>
                  <ArrowUpRight className="hidden h-7 w-7 text-navy/40 transition-all duration-700 group-hover:rotate-45 group-hover:text-gold-deep md:block" strokeWidth={1.25} />
                </Link>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-ivory section-lux">
        <div className="container-lux grid gap-28 lg:grid-cols-2 lg:gap-24">
          {[["Key Benefits", d.benefits, "destination-benefits"], ["Eligibility Snapshot", d.eligibility, "destination-eligibility"]].map(([title, items, id]) => (
            <div key={id} data-testid={id}>
              <SectionHeading eyebrow={title} lines={[id.endsWith("benefits") ? "Why choose it." : "What is typically required."]} />
              <ul className="mt-16 border-t border-navy/10">
                {items.map((b, i) => (
                  <Reveal as="li" key={b} delay={i * 0.06} className="flex gap-6 border-b border-navy/10 py-7 text-[17px] font-light leading-relaxed text-charcoal">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-gold-deep" />{b}
                  </Reveal>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="container-lux mt-16 text-[13px] text-charcoal-soft">Requirements vary by route and change from time to time. All pathways are subject to eligibility and the decision of the relevant authority.</p>
      </section>

      <section className="bg-ivory-100 section-lux" data-testid="destination-faq">
        <div className="container-lux grid gap-16 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
          <SectionHeading eyebrow="FAQ" lines={["Questions,", "answered plainly."]} />
          <FAQList items={d.faqs} prefix={`dest-faq`} />
        </div>
      </section>
      <CTABanner title={`Begin your ${d.short} journey with clarity.`} />
    </>
  );
}
